import React, { useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimes,
  faXmarkCircle,
} from "@fortawesome/free-solid-svg-icons";
import { validateVerificationCode } from "../../api/auth";
import { useNavigate } from "react-router-dom";

interface VerificationCodeModalProps {
  show: boolean;
  onHide: () => void;
  verificationCode: string;
  setVerificationCode: (code: string) => void;
  email: string;
}

const VerificationCodeModal: React.FC<VerificationCodeModalProps> = ({
  show,
  onHide,
  verificationCode,
  setVerificationCode,
  email,
}) => {
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  /**
   * Handles input changes for the verification code fields.
   * @param index - The index of the input field being changed.
   * @param value - The new value of the input field.
   */
  const handleInputChange = (index: number, value: string) => {
    // Check if the input is a single alphanumeric character
    if (value.length <= 1 && /^[a-zA-Z0-9]*$/.test(value)) {
      // Update the verification code at the specified index
      const newCode = verificationCode.split("");
      newCode[index] = value; // Keep original case as entered by user
      setVerificationCode(newCode.join(""));

      // Move focus to the next input field if available
      if (value && index < 5) {
        inputRefs[index + 1].current?.focus();
      }
    }
  };

  /**
   * Handles keydown events for the verification code fields.
   * @param index - The index of the input field where the key was pressed.
   * @param e - The keyboard event object.
   */
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Move focus to the previous input field on backspace if current field is empty
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  /**
   * Handles the submission of the verification code.
   */
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Validate the verification code
      const isValid = await validateVerificationCode(email, verificationCode);
      setIsCodeValid(isValid);

      if (isValid) {
        // If valid, wait 2 seconds then navigate to the change password page
        setTimeout(() => {
          onHide();
          navigate("/change-password", {
            state: {
              email: email,
              verificationCode: verificationCode,
            },
          });
        }, 2000);
      }
    } catch (error) {
      setIsCodeValid(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="verification-modal">
      <Modal.Header className="d-flex justify-content-between align-items-center">
        <Modal.Title>Recuperación</Modal.Title>
        {isCodeValid ? (
          <></>
        ) : (
          <Button variant="link" onClick={onHide} className="close-button">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </Modal.Header>
      <Modal.Body>
        <p className="text-center">Ingresa el código de verificación</p>
        <p className="text-center text-muted small">
          El código puede contener letras y números
        </p>
        <div className="d-flex justify-content-center">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <Form.Control
              key={index}
              ref={inputRefs[index]}
              type="text"
              maxLength={1}
              value={verificationCode[index] || ""}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(index, e)
              }
              className="verification-code-input text-center mx-1"
              style={{ width: "3rem", height: "3rem", fontSize: "1.5rem" }}
            />
          ))}
        </div>
        {isCodeValid !== null && (
          <div className="text-center mt-3">
            <FontAwesomeIcon
              icon={isCodeValid ? faCheckCircle : faXmarkCircle}
              className={isCodeValid ? "text-success" : "text-danger"}
              size="2x"
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {isCodeValid ? (
          <></>
        ) : (
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="mx-auto"
            disabled={isSubmitting || verificationCode.length !== 6}
          >
            {isSubmitting ? "Verificando..." : "Aceptar"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default VerificationCodeModal;
